export const careerRoles = [
  {
    id: "inference_kernels_engineer",
    tag: "Inference",
    title: "Inference & Kernels Engineer",
    subtitle: "The cost-per-token killer",
    location: "Mountain View",
    mandate:
      "Reduce inference cost and latency while preserving model quality and correctness. Own the path from kernel optimization to serving performance.",
    stack: [
      "Custom CUDA / Triton / ROCm kernels",
      "INT4 / FP8 quantization, speculative & parallel decoding",
      "MoE routing, KV-cache reuse, custom serving runtime",
    ],
    evidence:
      "Link to a benchmark or technical demonstration of a substantial improvement you made. Include the baseline, hardware, workload, correctness checks, and your contribution.",
  },
  {
    id: "agents_multi_agents_engineer",
    tag: "Agent systems",
    title: "Agents + Multi-Agents Engineer",
    subtitle: "Multi-agent coordination and concurrent multi-device use",
    location: "San Francisco",
    mandate:
      "Build digital humanoid agents and multi-agent teams that coordinate real work across computers, phones, browsers, and watches. Give them shared context and the ability to use multiple screens and devices concurrently, locally or remotely, with reliable handoffs and recovery when work fails.",
    stack: [
      "Multi-agent planning, delegation, and coordination",
      "Multi-screen and multi-device use: computers, phones, browsers, and watches",
      "Concurrent and parallel device execution with shared context and reliable handoffs",
      "Remote device use with secure access and persistent sessions",
      "Integrations on the fly for authenticated, API-less systems; reliable read and write actions",
    ],
    evidence:
      "Show us agent or multi-agent systems you built that reliably did real work at scale. Lead with coordinated agents, concurrent device use, or authenticated integrations that held up over thousands of runs. Show how your system shares context, resolves conflicts, and recovers from failures.",
  },
  {
    id: "desktop_worker_engineer",
    tag: "Application",
    title: "Desktop Worker Engineer",
    subtitle: "The product people trust with their everyday work",
    location: "San Francisco",
    mandate:
      "Build the desktop app that becomes the worker: the surface people trust to do real work on their machine. Fuse vision, hearing, voice, reasoning, and action into one reliable loop with real OS access. The bar is not a demo: a dependable product that a real person leaves running all day.",
    stack: [
      "Cross-platform desktop app with real OS and app access (native / Tauri)",
      "Orchestrate vision + hearing + voice + action into one dependable worker",
      "Local performance, permissions, security, and a UX people trust with real work",
    ],
    evidence:
      "Show us a product you built and shipped that people use every day, ideally something with real desktop or OS depth. A repo, a live app, or download numbers. Bonus if you've shipped an agent or automation surface people actually kept using.",
  },
  {
    id: "streaming_vision_engineer",
    tag: "Vision",
    title: "Streaming Vision Engineer",
    subtitle: "Real-time perception: seeing the screen like a human",
    location: "Palo Alto",
    mandate:
      "Give the worker eyes across devices. Perceive multiple screens, video streams, and documents in real time, locally or remotely: streaming visual understanding, UI grounding, and OCR fast and cheap enough to run every second of every worker-hour.",
    stack: [
      "Streaming vision: concurrent multi-screen and video understanding across devices",
      "UI grounding, element detection, layout parsing, and OCR",
      "Cheap visual tokens: distillation, caching, resolution scheduling",
    ],
    evidence:
      "Show us real-time vision you've shipped: screen understanding, video, OCR, or UI grounding at low latency and cost. Benchmarks, a live demo, or a repo.",
  },
  {
    id: "distributed_serving_fleet_engineer",
    tag: "Serving / Infra",
    title: "Distributed Serving & Fleet Engineer",
    subtitle: "100,000 workers on the cheapest compute on Earth",
    location: "Mountain View",
    mandate:
      "Run a planetary fleet of digital humanoid workers across devices at sub-cent economics. Coordinate concurrent local and remote device sessions, schedule work, and squeeze every cycle out of spot, edge, and homed nodes.",
    stack: [
      "Massive batching, prompt caching, KV-cache sharing",
      "Spot / edge / on-prem node orchestration & failover",
      "Concurrent device scheduling, remote sessions, and recovery",
      "Per-worker-hour cost accounting down to the cent",
    ],
    evidence:
      "Show us large-scale systems you've actually run: QPS, node counts, cost curves. If you've cut an infra bill 10× or run a fleet at massive scale, show the graph.",
  },
  {
    id: "realtime_voice_hearing_engineer",
    tag: "Audio",
    title: "Real-Time Voice & Hearing Engineer",
    subtitle: "Give the worker ears and a voice",
    location: "San Francisco",
    mandate:
      "Sub-200ms streaming listening and speech so the worker can hear, take calls, join meetings, and talk like a person, at near-zero cost per minute.",
    stack: [
      "Streaming STT (hearing) + TTS (speaking), low-latency models",
      "Barge-in, turn-taking, echo cancellation",
      "Full-duplex audio pipelines under hard latency budgets",
    ],
    evidence:
      "Show us real-time audio you've shipped: latency numbers, a live demo, or a repo. Bonus if you've fought turn-taking and echo cancellation and won.",
  },
  {
    id: "post_training_distillation_lead",
    tag: "Post-training",
    title: "Post-Training & Distillation Lead",
    subtitle: "Capability per dollar",
    location: "Palo Alto",
    mandate:
      "Distill frontier capability into tiny, specialized worker models, one per job type: background check, back office, research, support. Build the data flywheel off the live fleet.",
    stack: [
      "Distillation, RL on real task execution, reward modeling",
      "Eval harnesses tied to real job outcomes",
      "Data flywheel: every task makes the next one cheaper",
    ],
    evidence:
      "Show us models you've trained, fine-tuned, or distilled that punched far above their size and cost. Eval numbers, a paper, or weights.",
  },
  {
    id: "model_architecture_research_scientist",
    tag: "Model architecture",
    title: "Model Architecture Research Scientist",
    subtitle: "Invent the next architecture",
    location: "Palo Alto",
    mandate:
      "Invent, train, and validate original language-model architectures for digital humanoid workers. Our ambition is to outperform strong transformer baselines in useful capability, reliability, and efficiency, with sustained inference within 20 W for the whole inference device, including compute, memory, and device overhead. These are research targets to demonstrate through controlled experiments.",
    stack: [
      "Original model architectures, learning mechanisms, and training methods, from hypothesis to working model",
      "Reasoning, persistent memory, and long-horizon learning for workers acting across devices and applications",
      "Controlled comparisons with strong transformer baselines: task success, reliability, latency, throughput, and energy per successful task",
      "Hardware-aware model design and measured whole-device power under documented workloads and quality constraints",
    ],
    evidence:
      "Link to an original architecture, paper, model, or reproducible experiment you built. Explain your contribution and show baselines, ablations, and the results. For efficiency claims, report the hardware, workload, quality, latency, throughput, and how power was measured. Code is welcome when shareable. You do not need to have already achieved our 20 W target.",
  },
];
